import { useMemo } from "react";

import { useI18n as useCoreI18n } from "./I18nContext";
import { useLocale } from "./LocaleContext";
import { makeSafeI18n } from "./makeSafeI18n";
import type { TranslatedString } from "./TranslatedString";
import { type BrandedGettextInterface, brandGettext } from "./useBrandedI18n";

/** Placeholder substitutions for `{0}`, `{1}`, … — the gettext `args` parameter. */
type GettextArgs = (string | number)[] | string;

/**
 * Options form of the trailing gettext argument that also carries a `fallback`.
 *
 * `fallback` is returned (for non-English locales) when the primary msgid has no
 * translation yet — otherwise the call would render blank. It is typically
 * another, already-translated string:
 *
 * ```tsx
 * gettext("New heading", { fallback: gettext("Existing heading") });
 * gettext("Applied {0} days ago", { args: [days], fallback: gettext("Recently applied") });
 * ```
 */
export interface SafeGettextOptions {
  /**
   * Shown (non-English locales) when the primary msgid is untranslated. Must be
   * a `TranslatedString` — i.e. itself from gettext (an already-translated
   * string), so a fallback can never leak raw English. For a deliberate raw
   * literal, wrap it explicitly: `asTranslatedString("…")`.
   */
  fallback: TranslatedString;
  /** Placeholder substitutions for `{0}`, `{1}`, …, as with plain gettext. */
  args?: GettextArgs;
}

/**
 * `BrandedGettextInterface` whose string-returning calls also accept a trailing
 * `{ fallback }` options object (see {@link SafeGettextOptions}).
 */
export interface SafeGettextInterface extends Omit<
  BrandedGettextInterface,
  "gettext" | "pgettext" | "ngettext" | "npgettext"
> {
  gettext(msgId: string, args?: GettextArgs): TranslatedString;
  gettext(msgId: string, options: SafeGettextOptions): TranslatedString;
  pgettext(msgCtxt: string, msgId: string, args?: GettextArgs): TranslatedString;
  pgettext(msgCtxt: string, msgId: string, options: SafeGettextOptions): TranslatedString;
  ngettext(msgId: string, msgIdPlural: string, n: number, args?: GettextArgs): TranslatedString;
  ngettext(
    msgId: string,
    msgIdPlural: string,
    n: number,
    options: SafeGettextOptions,
  ): TranslatedString;
  npgettext(
    msgCtxt: string,
    msgId: string,
    msgIdPlural: string,
    n: number,
    args?: GettextArgs,
  ): TranslatedString;
  npgettext(
    msgCtxt: string,
    msgId: string,
    msgIdPlural: string,
    n: number,
    options: SafeGettextOptions,
  ): TranslatedString;
}

function isOptions(
  value: GettextArgs | SafeGettextOptions | undefined,
): value is SafeGettextOptions {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Internationalization hook that never leaks untranslated English to non-English
 * users. Same API as `useBrandedI18n`, but locale-aware:
 * - **English** (source locale, incl. `en_GB`/`en_CA`/bare-`en`): the English
 *   source copy for every msgid.
 * - **Any other locale**: the translation once the catalog has it, else an empty
 *   string — never the English msgid. Where blank is bad UX, pass a `fallback`
 *   (see {@link SafeGettextOptions}).
 *
 * Enforced in internationally-exposed scopes by `i18n/require-safe-i18n`
 * (see `internationalExposure.config.mjs`).
 */
export function useSafeI18n(): SafeGettextInterface {
  const coreI18n = useCoreI18n();
  const locale = useLocale();

  return useMemo(() => {
    const safe = brandGettext(makeSafeI18n(coreI18n, locale));

    // `makeSafeI18n` yields "" for an untranslated msgid in a non-English
    // locale; when the caller supplied a `fallback`, return that instead.
    const withFallback = (
      result: TranslatedString,
      trailing: GettextArgs | SafeGettextOptions | undefined,
    ): TranslatedString => (result === "" && isOptions(trailing) ? trailing.fallback : result);

    return {
      ...safe,
      gettext(msgId: string, trailing?: GettextArgs | SafeGettextOptions): TranslatedString {
        const args = isOptions(trailing) ? trailing.args : trailing;
        return withFallback(safe.gettext(msgId, args), trailing);
      },
      pgettext(
        msgCtxt: string,
        msgId: string,
        trailing?: GettextArgs | SafeGettextOptions,
      ): TranslatedString {
        const args = isOptions(trailing) ? trailing.args : trailing;
        return withFallback(safe.pgettext(msgCtxt, msgId, args), trailing);
      },
      ngettext(
        msgId: string,
        msgIdPlural: string,
        n: number,
        trailing?: GettextArgs | SafeGettextOptions,
      ): TranslatedString {
        const args = isOptions(trailing) ? trailing.args : trailing;
        return withFallback(safe.ngettext(msgId, msgIdPlural, n, args), trailing);
      },
      npgettext(
        msgCtxt: string,
        msgId: string,
        msgIdPlural: string,
        n: number,
        trailing?: GettextArgs | SafeGettextOptions,
      ): TranslatedString {
        const args = isOptions(trailing) ? trailing.args : trailing;
        return withFallback(safe.npgettext(msgCtxt, msgId, msgIdPlural, n, args), trailing);
      },
    };
  }, [coreI18n, locale]);
}
